/**
 * Degree Auto-Fill Engine
 *
 * Orchestrates the full auto-fill pipeline for degree plans:
 * 1. Uses greedy set cover (degreeOptimization.ts) to select the optimal course set
 * 2. Builds a prerequisite DAG and computes blocking/delay factors
 * 3. Assigns courses to semesters via topological sort with constraint satisfaction
 * 4. Runs a workload balancing pass to even out credits across semesters
 */

import { RutgersCourse, Semester, SemesterItem } from '@/app/types/interfaces';
import { calculateOptimalCourseSet } from './degreeOptimization';
import { parsePrerequisiteGroups } from './prerequisiteChecker';
import { normalize, isLikelyCourseCode } from './degreeRequirementService';

const AUTO_FILL_DEBUG = process.env.NEXT_PUBLIC_DEBUG_DEGREE_PLANNING === 'true';

function debugAutoFill(...args: unknown[]) {
    if (AUTO_FILL_DEBUG) {
        console.debug(...args);
    }
}

// ============================================================================
// TYPES
// ============================================================================

export interface AutoFillConfig {
    maxCreditsPerSemester: number;    // Default: 18
    minCreditsPerSemester: number;    // Default: 12
    startSeason: 'Fall' | 'Spring';
    startYear: number;
    completedCourses: string[];       // Normalized course codes already completed
    expandedPools: Map<string, string[]>;
    creditsMap: Map<string, number>;
    courseNameMap: Map<string, string>;
    degreeCourseListsDirect: Map<string, { classes: string[]; name?: string; description?: string }>;
    catalogCourses: RutgersCourse[];
}

export interface AutoFillCourseInfo {
    code: string;           // Original course code (e.g. "01198111")
    name: string;
    credits: number;
    requirements: string[]; // Which degree requirements this course satisfies
    blockingFactor: number; // How many downstream courses this unlocks
    delayFactor: number;    // Length of longest prerequisite chain starting here
    prerequisiteCodes: string[]; // Normalized codes of direct prerequisites
}

export interface AutoFillResult {
    semesters: Array<{
        name: string;
        number: number;
        courses: SemesterItem[];
        totalCredits: number;
    }>;
    courseListPlacements: Array<{
        listName: string;
        label: string;
        semesterNumber: number;
    }>;
    stats: {
        totalCourses: number;
        totalCredits: number;
        semesterCount: number;
        coursesDoubledDipped: number;
        averageCreditsPerSemester: number;
        creditStdDev: number;
    };
}

// Internal node in the prerequisite DAG
interface DAGNode {
    code: string;                    // Normalized course code
    originalCode: string;            // Original format
    name: string;
    credits: number;
    prerequisites: Set<string>;      // Normalized codes of direct prereqs (only those in our selected set)
    dependents: Set<string>;         // Courses that depend on this one
    blockingFactor: number;          // Transitive count of downstream courses
    delayFactor: number;             // Length of longest chain from here to a leaf
    assignedSemester: number;        // Which semester index (0-based)
    requirements: string[];
    isAutoInsertedPrerequisite: boolean;
}

// ============================================================================
// MAIN API
// ============================================================================

/**
 * Generates an optimized auto-fill plan for the given degree requirements.
 */
export function generateAutoFillPlan(
    mergedRequirements: any[],
    config: AutoFillConfig
): AutoFillResult {
    debugAutoFill('[AutoFill] Starting pipeline for', mergedRequirements?.length || 0, 'requirements');
    
    // Step 1: Select optimal courses
    debugAutoFill('[AutoFill] Step 1: Optimal Course Selection');
    const { selectedCourses, courseListPlacements, requirementSatisfaction } =
        selectOptimalCourses(mergedRequirements, config);
    
    debugAutoFill('[AutoFill] Selected', selectedCourses.length, 'courses and', courseListPlacements.length, 'list blocks');

    if (selectedCourses.length === 0 && courseListPlacements.length === 0) {
        console.warn('[AutoFill] No courses selected to satisfy requirements.');
        return emptyResult();
    }

    // Step 2: Build prerequisite DAG
    debugAutoFill('[AutoFill] Step 2: Building Prerequisite DAG');
    const dag = buildPrerequisiteDAG(selectedCourses, config);
    debugAutoFill('[AutoFill] DAG built with', dag.size, 'nodes');

    // Step 3: Compute blocking/delay factors
    debugAutoFill('[AutoFill] Step 3: Computing Priorities');
    computeBlockingFactors(dag);
    computeDelayFactors(dag);

    // Step 4: Assign to semesters using topological sort
    debugAutoFill('[AutoFill] Step 4: Semester Assignment');
    const semesters = assignCoursesToSemesters(dag, config);
    debugAutoFill('[AutoFill] Courses assigned to', semesters.length, 'semesters');

    // Step 5: Place course list blocks
    debugAutoFill('[AutoFill] Step 5: Placement of course lists');
    placeCourseListBlocks(semesters, courseListPlacements, config);

    // Step 6: Final workload balancing
    debugAutoFill('[AutoFill] Step 6: Workload Balancing');
    balanceWorkloads(semesters, dag, config);

    const stats = computeStats(semesters, requirementSatisfaction);
    debugAutoFill('[AutoFill] Pipeline complete. Stats:', stats);

    return {
        semesters,
        courseListPlacements: courseListPlacements.map(cl => ({
            listName: cl.listName,
            label: cl.label,
            semesterNumber: cl.assignedSemester
        })),
        stats
    };
}

function emptyResult(): AutoFillResult {
    return {
        semesters: [],
        courseListPlacements: [],
        stats: {
            totalCourses: 0,
            totalCredits: 0,
            semesterCount: 0,
            coursesDoubledDipped: 0,
            averageCreditsPerSemester: 0,
            creditStdDev: 0
        }
    };
}

// ============================================================================
// STEP 1: OPTIMAL COURSE SELECTION
// ============================================================================

interface SelectedCourseInfo {
    code: string;
    normalizedCode: string;
    name: string;
    credits: number;
    requirements: string[];
    isAutoInsertedPrerequisite?: boolean;
}

interface CourseListPlacement {
    listName: string;
    label: string;
    courses: string[];
    requirements: string[];
    assignedSemester: number;
}

function selectOptimalCourses(
    mergedRequirements: any[],
    config: AutoFillConfig
): {
    selectedCourses: SelectedCourseInfo[];
    courseListPlacements: CourseListPlacement[];
    requirementSatisfaction: Map<string, any>;
} {
    const allCourses = config.catalogCourses;
    if (!allCourses || allCourses.length === 0) {
        return { selectedCourses: [], courseListPlacements: [], requirementSatisfaction: new Map() };
    }

    // Build course universe
    const courseUniverse = new Map<string, { credits: number; name?: string }>();
    for (const course of allCourses) {
        const code = course.baseId || course.id || '';
        if (!code) continue;
        const normalized = normalize(code);
        const credits = typeof course.credits === 'string'
            ? parseFloat(course.credits)
            : Number(course.credits) || 3;

        if (!courseUniverse.has(normalized) || course.courseName) {
            courseUniverse.set(normalized, {
                credits: Number.isFinite(credits) && credits > 0 ? credits : 3,
                name: course.courseName || config.courseNameMap.get(normalized)
            });
        }
    }

    // Run set cover optimization
    const requirementsArray = Array.isArray(mergedRequirements)
        ? mergedRequirements
        : (mergedRequirements as any)?.requirements || [];

    if (requirementsArray.length === 0 || courseUniverse.size === 0) {
        return { selectedCourses: [], courseListPlacements: [], requirementSatisfaction: new Map() };
    }

    const optimalResult = calculateOptimalCourseSet(
        requirementsArray,
        config.completedCourses,
        config.expandedPools,
        courseUniverse,
        normalize
    );

    // Map results to SelectedCourseInfo
    const selectedCourses: SelectedCourseInfo[] = optimalResult.courses.map(courseCode => {
        const normalizedCode = normalize(courseCode);
        const courseInfo = courseUniverse.get(normalizedCode);
        const requirementsSatisfied: string[] = [];

        optimalResult.requirementSatisfaction.forEach((satisfaction, reqId) => {
            if (satisfaction.coursesUsed.some((c: string) => normalize(c) === normalizedCode)) {
                requirementsSatisfied.push(reqId);
            }
        });

        return {
            code: courseCode,
            normalizedCode,
            name: courseInfo?.name || config.courseNameMap.get(normalizedCode) || courseCode,
            credits: courseInfo?.credits || 3,
            requirements: requirementsSatisfied
        };
    });

    // Identify course list placements (blocks that represent "pick from this list")
    const courseListPlacements: CourseListPlacement[] = [];
    const courseListRefs = new Set<string>();

    const collectCourseListRefs = (reqs: any[]) => {
        for (const req of reqs) {
            if (Array.isArray(req.course_pool)) {
                for (const entry of req.course_pool) {
                    if (!isLikelyCourseCode(entry) && config.expandedPools.has(entry)) {
                        courseListRefs.add(entry);
                    }
                }
            }
            if (Array.isArray(req.requirements)) {
                collectCourseListRefs(req.requirements);
            }
        }
    };
    collectCourseListRefs(requirementsArray);

    // Check which course list references weren't fully resolved by specific courses
    const selectedNormalized = new Set(selectedCourses.map(c => c.normalizedCode));
    for (const listName of courseListRefs) {
        const listCourses = config.expandedPools.get(listName) || [];
        const listCoursesNormalized = new Set(listCourses.map(normalize));

        // Check if any selected course comes from this list
        const hasExplicitCourse = selectedCourses.some(sc => listCoursesNormalized.has(sc.normalizedCode));

        if (!hasExplicitCourse) {
            // No specific course picked — add as a course list block
            const listData = config.degreeCourseListsDirect.get(listName);
            courseListPlacements.push({
                listName,
                label: listData?.name || listName,
                courses: listCourses,
                requirements: [listName],
                assignedSemester: -1 // Will be assigned in step 5
            });
        }
    }

    const expandedSelectedCourses = expandSelectedCoursesWithPrerequisites(selectedCourses, config, courseUniverse);

    return {
        selectedCourses: expandedSelectedCourses,
        courseListPlacements,
        requirementSatisfaction: optimalResult.requirementSatisfaction
    };
}

interface PrerequisiteBranchResolution {
    addedCourses: Map<string, SelectedCourseInfo>;
    completedChoices: number;
    selectedChoices: number;
    chainLength: number;
    addedCredits: number;
}

function expandSelectedCoursesWithPrerequisites(
    selectedCourses: SelectedCourseInfo[],
    config: AutoFillConfig,
    courseUniverse: Map<string, { credits: number; name?: string }>
): SelectedCourseInfo[] {
    const allCoursesMap = new Map<string, RutgersCourse>();
    for (const course of config.catalogCourses) {
        allCoursesMap.set(normalize(course.baseId || course.id || ''), course);
    }

    const completedSet = new Set(config.completedCourses.map(normalize));
    const selectedMap = new Map<string, SelectedCourseInfo>(
        selectedCourses.map((course) => [course.normalizedCode, course])
    );
    const resolutionCache = new Map<string, PrerequisiteBranchResolution>();

    const emptyResolution = (): PrerequisiteBranchResolution => ({
        addedCourses: new Map(),
        completedChoices: 0,
        selectedChoices: 0,
        chainLength: 0,
        addedCredits: 0,
    });

    const compareResolutions = (
        left: PrerequisiteBranchResolution | null,
        right: PrerequisiteBranchResolution | null
    ): number => {
        if (!left && !right) return 0;
        if (!left) return 1;
        if (!right) return -1;
        if (left.completedChoices !== right.completedChoices) {
            return right.completedChoices - left.completedChoices;
        }
        if (left.selectedChoices !== right.selectedChoices) {
            return right.selectedChoices - left.selectedChoices;
        }
        if (left.chainLength !== right.chainLength) {
            return left.chainLength - right.chainLength;
        }
        if (Math.abs(left.addedCredits - right.addedCredits) > 0.001) {
            return left.addedCredits - right.addedCredits;
        }
        return left.addedCourses.size - right.addedCourses.size;
    };

    const mergeResolutions = (
        base: PrerequisiteBranchResolution,
        addition: PrerequisiteBranchResolution
    ): PrerequisiteBranchResolution => {
        const mergedCourses = new Map(base.addedCourses);
        addition.addedCourses.forEach((course, code) => {
            if (!mergedCourses.has(code)) {
                mergedCourses.set(code, course);
            }
        });

        return {
            addedCourses: mergedCourses,
            completedChoices: base.completedChoices + addition.completedChoices,
            selectedChoices: base.selectedChoices + addition.selectedChoices,
            chainLength: Math.max(base.chainLength, addition.chainLength),
            addedCredits: Array.from(mergedCourses.values()).reduce(
                (sum, course) => sum + (course.credits || 0),
                0
            ),
        };
    };

    const resolveCourse = (courseCode: string, stack = new Set<string>()): PrerequisiteBranchResolution => {
        const normalizedCode = normalize(courseCode);
        if (!normalizedCode) return emptyResolution();
        if (completedSet.has(normalizedCode)) {
            return { ...emptyResolution(), completedChoices: 1 };
        }
        if (selectedMap.has(normalizedCode)) {
            return { ...emptyResolution(), selectedChoices: 1 };
        }
        if (resolutionCache.has(normalizedCode)) {
            return resolutionCache.get(normalizedCode)!;
        }
        if (stack.has(normalizedCode)) {
            return emptyResolution();
        }

        const catalogCourse = allCoursesMap.get(normalizedCode);
        const courseInfo = courseUniverse.get(normalizedCode);
        const currentCourse: SelectedCourseInfo = {
            code: catalogCourse?.baseId || normalizedCode,
            normalizedCode,
            name: catalogCourse?.courseName || courseInfo?.name || config.courseNameMap.get(normalizedCode) || (catalogCourse?.baseId || normalizedCode),
            credits: courseInfo?.credits || (typeof catalogCourse?.credits === 'string' ? parseFloat(catalogCourse.credits) : Number(catalogCourse?.credits)) || 3,
            requirements: [],
            isAutoInsertedPrerequisite: true,
        };

        const nextStack = new Set(stack);
        nextStack.add(normalizedCode);

        let bestResolution = emptyResolution();
        bestResolution.addedCourses.set(normalizedCode, currentCourse);
        bestResolution.chainLength = 1;
        bestResolution.addedCredits = currentCourse.credits;

        const prereqGroups = catalogCourse?.prerequisites
            ? parsePrerequisiteGroups(catalogCourse.prerequisites)
            : [];

        if (prereqGroups.length > 0) {
            let bestGroupResolution: PrerequisiteBranchResolution | null = null;

            for (const group of prereqGroups) {
                let groupResolution = emptyResolution();
                let validGroup = true;

                for (const clause of group) {
                    const candidateCodes = clause
                        .filter((entry) => !entry.startsWith('>='))
                        .map((entry) => normalize(entry));

                    if (candidateCodes.length === 0) {
                        continue;
                    }

                    let bestClauseResolution: PrerequisiteBranchResolution | null = null;
                    for (const candidateCode of candidateCodes) {
                        const candidateResolution = resolveCourse(candidateCode, nextStack);
                        if (compareResolutions(candidateResolution, bestClauseResolution) < 0) {
                            bestClauseResolution = candidateResolution;
                        }
                    }

                    if (!bestClauseResolution) {
                        validGroup = false;
                        break;
                    }

                    groupResolution = mergeResolutions(groupResolution, bestClauseResolution);
                }

                if (!validGroup) {
                    continue;
                }

                if (groupResolution.chainLength > 0) {
                    groupResolution.chainLength += 1;
                }

                if (compareResolutions(groupResolution, bestGroupResolution) < 0) {
                    bestGroupResolution = groupResolution;
                }
            }

            if (bestGroupResolution) {
                bestResolution = mergeResolutions(bestResolution, bestGroupResolution);
                bestResolution.chainLength = Math.max(bestResolution.chainLength, bestGroupResolution.chainLength);
            }
        }

        resolutionCache.set(normalizedCode, bestResolution);
        return bestResolution;
    };

    for (const selectedCourse of [...selectedMap.values()]) {
        const catalogCourse = allCoursesMap.get(selectedCourse.normalizedCode);
        const prereqGroups = catalogCourse?.prerequisites
            ? parsePrerequisiteGroups(catalogCourse.prerequisites)
            : [];

        if (prereqGroups.length === 0) {
            continue;
        }

        let bestGroupResolution: PrerequisiteBranchResolution | null = null;
        for (const group of prereqGroups) {
            let groupResolution = emptyResolution();
            let validGroup = true;

            for (const clause of group) {
                const candidateCodes = clause
                    .filter((entry) => !entry.startsWith('>='))
                    .map((entry) => normalize(entry));

                if (candidateCodes.length === 0) {
                    continue;
                }

                let bestClauseResolution: PrerequisiteBranchResolution | null = null;
                for (const candidateCode of candidateCodes) {
                    const candidateResolution = resolveCourse(candidateCode, new Set([selectedCourse.normalizedCode]));
                    if (compareResolutions(candidateResolution, bestClauseResolution) < 0) {
                        bestClauseResolution = candidateResolution;
                    }
                }

                if (!bestClauseResolution) {
                    validGroup = false;
                    break;
                }

                groupResolution = mergeResolutions(groupResolution, bestClauseResolution);
            }

            if (!validGroup) {
                continue;
            }

            if (compareResolutions(groupResolution, bestGroupResolution) < 0) {
                bestGroupResolution = groupResolution;
            }
        }

        bestGroupResolution?.addedCourses.forEach((course, code) => {
            if (!completedSet.has(code) && !selectedMap.has(code)) {
                selectedMap.set(code, course);
            }
        });
    }

    return Array.from(selectedMap.values());
}

// ============================================================================
// STEP 2: PREREQUISITE DAG CONSTRUCTION
// ============================================================================

function buildPrerequisiteDAG(
    selectedCourses: SelectedCourseInfo[],
    config: AutoFillConfig
): Map<string, DAGNode> {
    const dag = new Map<string, DAGNode>();
    const allCourses = config.catalogCourses;
    const allCoursesMap = new Map<string, RutgersCourse>();

    for (const c of allCourses) {
        allCoursesMap.set(normalize(c.baseId), c);
    }

    // Build the selected course set for quick lookup
    const selectedSet = new Set(selectedCourses.map(c => c.normalizedCode));
    // Also include completed courses — they're "already placed" but we need them for prereq checking
    const completedSet = new Set(config.completedCourses.map(normalize));

    // Create DAG nodes for each selected course
    for (const sc of selectedCourses) {
        const node: DAGNode = {
            code: sc.normalizedCode,
            originalCode: sc.code,
            name: sc.name,
            credits: sc.credits,
            prerequisites: new Set<string>(),
            dependents: new Set<string>(),
            blockingFactor: 0,
            delayFactor: 0,
            assignedSemester: -1,
            requirements: sc.requirements,
            isAutoInsertedPrerequisite: !!sc.isAutoInsertedPrerequisite
        };
        dag.set(sc.normalizedCode, node);
    }

    // Extract prerequisite edges
    for (const sc of selectedCourses) {
        const catalogCourse = allCoursesMap.get(sc.normalizedCode);
        if (!catalogCourse?.prerequisites) continue;

        const prereqGroups = parsePrerequisiteGroups(catalogCourse.prerequisites);
        if (prereqGroups.length === 0) continue;

        // Take the first valid group (the simplest set of prerequisites)
        // Each group is OR-connected, so we only need to satisfy one group
        const extractPrereqCodes = (groups: string[][][]): string[] => {
            const codes: string[] = [];
            // Try to find a group where all clauses have at least one course in our set or completed
            for (const group of groups) {
                const groupCodes: string[] = [];
                let groupValid = true;

                for (const clause of group) {
                    // Each clause is an array of OR-choices within the clause
                    // Pick the first one that's in our set or completed
                    const cleanCodes = clause
                        .filter(c => !c.startsWith('>='))
                        .map(c => normalize(c.replace(/:/g, '')));

                    const inSet = cleanCodes.find(c => selectedSet.has(c) || completedSet.has(c));
                    if (inSet) {
                        groupCodes.push(inSet);
                    } else if (cleanCodes.length > 0) {
                        // None of the options are in our selected set — try anyway with first
                        groupCodes.push(cleanCodes[0]);
                    } else {
                        groupValid = false;
                    }
                }

                if (groupValid && groupCodes.length > 0) {
                    codes.push(...groupCodes);
                    break; // Use first valid group
                }
            }

            // Fallback: if no group worked, just extract all codes from first group
            if (codes.length === 0 && groups.length > 0) {
                for (const clause of groups[0]) {
                    const cleanCodes = clause
                        .filter(c => !c.startsWith('>='))
                        .map(c => normalize(c.replace(/:/g, '')));
                    codes.push(...cleanCodes);
                }
            }

            return codes;
        };

        const prereqCodes = extractPrereqCodes(prereqGroups);
        const node = dag.get(sc.normalizedCode)!;

        for (const prereqCode of prereqCodes) {
            // Only add prerequisite edges for courses that are in the selected set
            // (completed courses are already done — no need to track them)
            if (selectedSet.has(prereqCode) && prereqCode !== sc.normalizedCode) {
                node.prerequisites.add(prereqCode);

                const prereqNode = dag.get(prereqCode);
                if (prereqNode) {
                    prereqNode.dependents.add(sc.normalizedCode);
                }
            }
        }
    }

    return dag;
}

// ============================================================================
// STEP 3: BLOCKING & DELAY FACTOR COMPUTATION
// ============================================================================

function computeBlockingFactors(dag: Map<string, DAGNode>): void {
    // For each node, blocking factor = count of all transitive dependents
    const computeTransitiveDependents = (
        nodeCode: string,
        visited: Set<string>
    ): Set<string> => {
        if (visited.has(nodeCode)) return new Set<string>();
        visited.add(nodeCode);

        const node = dag.get(nodeCode);
        if (!node) return new Set<string>();

        const allDependents = new Set<string>(node.dependents);
        for (const dep of node.dependents) {
            const transitive = computeTransitiveDependents(dep, visited);
            for (const t of transitive) {
                allDependents.add(t);
            }
        }
        return allDependents;
    };

    for (const [code, node] of dag) {
        const allDeps = computeTransitiveDependents(code, new Set<string>());
        node.blockingFactor = allDeps.size;
    }
}

function computeDelayFactors(dag: Map<string, DAGNode>): void {
    // Delay factor = length of longest chain starting at this node
    const memo = new Map<string, number>();

    const longestChain = (nodeCode: string, visited: Set<string>): number => {
        if (visited.has(nodeCode)) return 0; // cycle guard
        if (memo.has(nodeCode)) return memo.get(nodeCode)!;
        visited.add(nodeCode);

        const node = dag.get(nodeCode);
        if (!node || node.dependents.size === 0) {
            memo.set(nodeCode, 0);
            visited.delete(nodeCode);
            return 0;
        }

        let maxChain = 0;
        for (const dep of node.dependents) {
            const chain = longestChain(dep, visited);
            maxChain = Math.max(maxChain, chain + 1);
        }

        memo.set(nodeCode, maxChain);
        visited.delete(nodeCode);
        return maxChain;
    };

    for (const [code, node] of dag) {
        node.delayFactor = longestChain(code, new Set<string>());
    }
}

// ============================================================================
// STEP 4: SEMESTER ASSIGNMENT VIA TOPOLOGICAL SORT
// ============================================================================

function assignCoursesToSemesters(
    dag: Map<string, DAGNode>,
    config: AutoFillConfig
): AutoFillResult['semesters'] {
    const semesters: AutoFillResult['semesters'] = [];

    // Get topologically-sorted course list, prioritizing by criticity score
    const sortedCourses = topologicalSortWithPriority(dag);

    // Helper: generate semester name
    // Fall start: Fall N, Spring N+1, Fall N+1, Spring N+2, ...
    // Spring start: Spring N, Fall N, Spring N+1, Fall N+1, ...
    const getSemesterName = (index: number): string => {
        // Fix: Use index directly to determine season correctly relative to start
        if (config.startSeason === 'Fall') {
            const season = index % 2 === 0 ? 'Fall' : 'Spring';
            const year = config.startSeason === 'Fall'
                ? config.startYear + Math.floor((index + 1) / 2)
                : config.startYear + Math.floor(index / 2);
            return `${season} ${year}`;
        } else {
            // Spring start
            const season = index % 2 === 0 ? 'Spring' : 'Fall';
            const year = config.startYear + Math.floor(index / 2);
            return `${season} ${year}`;
        }
    };

    // Helper: ensure semester exists
    const ensureSemester = (index: number) => {
        while (semesters.length <= index) {
            semesters.push({
                name: getSemesterName(semesters.length),
                number: semesters.length + 1,
                courses: [],
                totalCredits: 0
            });
        }
        return semesters[index];
    };

    // Helper: get current credits in a semester
    const getSemesterCredits = (semIndex: number): number => {
        if (semIndex >= semesters.length) return 0;
        return semesters[semIndex].totalCredits;
    };

    // Assign each course to the earliest feasible semester
    for (const courseCode of sortedCourses) {
        const node = dag.get(courseCode);
        if (!node) continue;

        // Find the earliest semester where prerequisites are satisfied
        let minSemester = 0;
        for (const prereqCode of node.prerequisites) {
            const prereqNode = dag.get(prereqCode);
            if (prereqNode && prereqNode.assignedSemester >= 0) {
                // Must be at least one semester after the prerequisite
                minSemester = Math.max(minSemester, prereqNode.assignedSemester + 1);
            }
        }

        // Find a semester from minSemester that has space
        let targetSemester = minSemester;
        for (let i = minSemester; i < 20; i++) { // Cap at 20 to prevent infinite loops
            ensureSemester(i);
            if (getSemesterCredits(i) + node.credits <= config.maxCreditsPerSemester) {
                targetSemester = i;
                break;
            }
            targetSemester = i + 1;
        }

        // Place the course
        const sem = ensureSemester(targetSemester);
        const item: SemesterItem = {
            kind: 'course',
            code: node.originalCode,
            name: node.name,
            credits: node.credits,
            semester: sem.number,
            completed: false,
            isAutoInsertedPrerequisite: node.isAutoInsertedPrerequisite
        };
        sem.courses.push(item);
        sem.totalCredits += node.credits;
        node.assignedSemester = targetSemester;
    }

    return semesters;
}

/**
 * Returns courses in topological order.
 * Among courses at the same topological level, prioritize by:
 *   1. Delay factor (longest chain) — higher = earlier
 *   2. Blocking factor (transitive dependents) — higher = earlier
 *   3. Credits (fewer = fill more efficiently)
 */
function topologicalSortWithPriority(dag: Map<string, DAGNode>): string[] {
    const result: string[] = [];
    const inDegree = new Map<string, number>();
    const queue: string[] = [];

    // Initialize in-degrees
    for (const [code, node] of dag) {
        inDegree.set(code, node.prerequisites.size);
        if (node.prerequisites.size === 0) {
            queue.push(code);
        }
    }

    while (queue.length > 0) {
        // Sort the queue by priority: highest delay factor first, then blocking factor
        queue.sort((a, b) => {
            const nodeA = dag.get(a);
            const nodeB = dag.get(b);
            if (!nodeA || !nodeB) return 0;
            
            // Critical first: longest path (delayFactor)
            if (nodeA.delayFactor !== nodeB.delayFactor) {
                return nodeB.delayFactor - nodeA.delayFactor;
            }
            // Then most dependents (blockingFactor)
            if (nodeA.blockingFactor !== nodeB.blockingFactor) {
                return nodeB.blockingFactor - nodeA.blockingFactor;
            }
            // Lower credits = more efficient to place    
            return (nodeA.credits || 0) - (nodeB.credits || 0);
        });

        const current = queue.shift();
        if (!current) break;
        result.push(current);

        const node = dag.get(current)!;
        for (const dep of node.dependents) {
            const deg = (inDegree.get(dep) || 0) - 1;
            inDegree.set(dep, deg);
            if (deg === 0) {
                queue.push(dep);
            }
        }
    }

    // Handle any nodes not reached (cycles) — just append them
    for (const [code] of dag) {
        if (!result.includes(code)) {
            result.push(code);
        }
    }

    return result;
}

// ============================================================================
// STEP 5: COURSE LIST BLOCK PLACEMENT
// ============================================================================

function placeCourseListBlocks(
    semesters: AutoFillResult['semesters'],
    courseListPlacements: CourseListPlacement[],
    config: AutoFillConfig
): void {
    const APPROX_CREDITS_PER_LIST = 3;

    for (const clp of courseListPlacements) {
        let placed = false;

        for (let i = 0; i < semesters.length; i++) {
            const sem = semesters[i];
            if (sem.totalCredits + APPROX_CREDITS_PER_LIST <= config.maxCreditsPerSemester) {
                const item: SemesterItem = {
                    kind: 'course_list',
                    mappingName: clp.listName,
                    courseListId: 0,
                    label: clp.label,
                    quantity: 1,
                    semester: sem.number,
                    completed: false
                };
                sem.courses.push(item);
                sem.totalCredits += APPROX_CREDITS_PER_LIST;
                clp.assignedSemester = sem.number;
                placed = true;
                break;
            }
        }

        // If no existing semester has space, create a new one
        if (!placed) {
            const idx = semesters.length;
            let name: string;
            if (config.startSeason === 'Fall') {
                const season = idx % 2 === 0 ? 'Fall' : 'Spring';
                const year = config.startYear + Math.floor((idx + 1) / 2);
                name = `${season} ${year}`;
            } else {
                const season = idx % 2 === 0 ? 'Spring' : 'Fall';
                const year = config.startYear + Math.floor(idx / 2);
                name = `${season} ${year}`;
            }

            const newSem = {
                name,
                number: semesters.length + 1,
                courses: [] as SemesterItem[],
                totalCredits: 0
            };

            const item: SemesterItem = {
                kind: 'course_list',
                mappingName: clp.listName,
                courseListId: 0,
                label: clp.label,
                quantity: 1,
                semester: newSem.number,
                completed: false
            };
            newSem.courses.push(item);
            newSem.totalCredits += APPROX_CREDITS_PER_LIST;
            clp.assignedSemester = newSem.number;
            semesters.push(newSem);
        }
    }
}

// ============================================================================
// STEP 6: WORKLOAD BALANCING
// ============================================================================

function balanceWorkloads(
    semesters: AutoFillResult['semesters'],
    dag: Map<string, DAGNode>,
    config: AutoFillConfig
): void {
    if (semesters.length <= 1) return;

    const totalCredits = semesters.reduce((sum, s) => sum + s.totalCredits, 0);
    const avgCredits = totalCredits / semesters.length;

    // Try to move courses from overloaded semesters to underloaded ones
    // Only if doing so doesn't violate prerequisites
    const MAX_ITERATIONS = 10;

    for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
        let improved = false;

        // Find the most overloaded and most underloaded semesters
        let maxCredits = -1, maxIdx = -1;
        let minCredits = Infinity, minIdx = -1;

        for (let i = 0; i < semesters.length; i++) {
            if (semesters[i].totalCredits > maxCredits) {
                maxCredits = semesters[i].totalCredits;
                maxIdx = i;
            }
            if (semesters[i].totalCredits < minCredits) {
                minCredits = semesters[i].totalCredits;
                minIdx = i;
            }
        }

        // Not worth balancing if the difference is small
        if (maxCredits - minCredits <= 3) break;

        // Try to move a course from maxIdx to minIdx
        // Only consider courses in the overloaded semester
        const overloaded = semesters[maxIdx];

        for (let ci = overloaded.courses.length - 1; ci >= 0; ci--) {
            const item = overloaded.courses[ci];
            if (item.kind !== 'course') continue;

            const courseNode = dag.get(normalize(item.code));
            if (!courseNode) continue;

            // Check: can this course be moved to minIdx?
            // 1. minIdx must be >= all prerequisite semesters + 1
            // 2. minIdx must be < all dependent semesters
            // 3. Only move forward (to a later semester that is underloaded)
            if (minIdx <= maxIdx) continue; // Only move to later semesters to keep prereqs valid

            let canMove = true;

            // Check prerequisites: all prereqs must be in semesters before minIdx
            for (const prereqCode of courseNode.prerequisites) {
                const prereqNode = dag.get(prereqCode);
                if (prereqNode && prereqNode.assignedSemester >= minIdx) {
                    canMove = false;
                    break;
                }
            }

            if (!canMove) continue;

            // Check dependents: all dependents must be in semesters after minIdx
            for (const depCode of courseNode.dependents) {
                const depNode = dag.get(depCode);
                if (depNode && depNode.assignedSemester <= minIdx) {
                    canMove = false;
                    break;
                }
            }

            if (!canMove) continue;

            // Check credit cap
            if (semesters[minIdx].totalCredits + item.credits > config.maxCreditsPerSemester) continue;

            // Move the course
            overloaded.courses.splice(ci, 1);
            overloaded.totalCredits -= item.credits;

            const movedItem = { ...item, semester: semesters[minIdx].number };
            semesters[minIdx].courses.push(movedItem);
            semesters[minIdx].totalCredits += item.credits;

            courseNode.assignedSemester = minIdx;
            improved = true;
            break; // One move per iteration
        }

        if (!improved) break;
    }
}

// ============================================================================
// STATS & HELPERS
// ============================================================================

function computeStats(
    semesters: AutoFillResult['semesters'],
    requirementSatisfaction: Map<string, any>
): AutoFillResult['stats'] {
    const totalCourses = semesters.reduce(
        (sum, s) => sum + s.courses.filter(c => c.kind === 'course').length,
        0
    );
    const totalCredits = semesters.reduce((sum, s) => sum + s.totalCredits, 0);
    const semesterCount = semesters.length;
    const avgCredits = semesterCount > 0 ? totalCredits / semesterCount : 0;

    // Count courses that satisfy 2+ requirements
    let coursesDoubledDipped = 0;
    const courseReqCount = new Map<string, number>();

    requirementSatisfaction.forEach((satisfaction) => {
        for (const course of satisfaction.coursesUsed) {
            const count = (courseReqCount.get(normalize(course)) || 0) + 1;
            courseReqCount.set(normalize(course), count);
        }
    });

    for (const count of courseReqCount.values()) {
        if (count >= 2) coursesDoubledDipped++;
    }

    // Credit standard deviation
    const creditValues = semesters.map(s => s.totalCredits);
    const variance =
        creditValues.reduce((sum, v) => sum + (v - avgCredits) ** 2, 0) / Math.max(semesterCount, 1);
    const creditStdDev = Math.sqrt(variance);

    return {
        totalCourses,
        totalCredits,
        semesterCount,
        coursesDoubledDipped,
        averageCreditsPerSemester: Math.round(avgCredits * 10) / 10,
        creditStdDev: Math.round(creditStdDev * 10) / 10
    };
}
